import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { round, uniq } from 'lodash'
import ReactECharts from 'echarts-for-react'
import { Dialog, DialogContent, Grid, Card, Box, Typography, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { aquaFetch } from 'common/utils/ActionUtils'
import { formatMilliers } from 'common/utils/StringUtil'
import useFetch from 'common/hooks/useFetch'
import ApplicationConf from 'conf/ApplicationConf'
import { STATUS } from 'common/constants/storeConstants'
import { BasicDialogTitle } from 'common/components/modal/DialogComponents'
import { MONTH } from '../StatisticsUtil'

// instanceType des produits clients (mêmes que les boutons produit du filtre). Sert à reconstituer
// l'ensemble « tous les projets clients » (union des MANTIS_NAME de leurs instances) pour le délai
// moyen de résolution des anomalies (#5), indépendant du filtre de projets.
const CLIENT_PRODUCTS = ['Myliaq', 'Seqoya', 'Iryqua', 'Acycliq']

// 6 indicateurs de la popin, alignés sur la série renvoyée par /support/stats/evolution.
const SUPPORT_KPIS = [
    { key: 'nbBlockingUrgentAnomalies', label: 'Anomalies bloquantes / urgentes ouvertes', unit: '', description: 'Anomalies créées dans le mois, de sévérité Bloquant OU de priorité Urgente/Immédiate.' },
    { key: 'nbBlockingResolvedOver3d', label: 'Bugs bloquants résolus à +3 j ouvrés', unit: '', description: 'Anomalies de sévérité Critique ou Bloquant résolues dans le mois avec un délai de résolution supérieur à 3 jours ouvrés.' },
    { key: 'stockAnomalies', label: 'Stock d\'anomalies', unit: '', description: 'Anomalies créées et encore ouvertes à la fin du mois (réouvertures incluses).' },
    { key: 'stockAssistance', label: 'Stock d\'assistance', unit: '', description: 'Tickets d\'assistance créés et encore ouverts à la fin du mois (réouvertures incluses).' },
    { key: 'avgResolutionAnomalies', label: 'Délai moyen de résolution des anomalies', unit: 'j', description: 'Délai moyen (jours ouvrés) de résolution des anomalies résolues dans le mois, sur tous les projets clients — indépendant du filtre de projets.' },
    { key: 'recurrenceRate', label: 'Taux de ticket récurrent', unit: '%', description: 'Part des tickets (anomalie + assistance) créés dans le mois ayant été rouverts au moins une fois.' },
]

// "MM-yyyy" -> "Mmm - yyyy"
const monthLabel = (date) => {
    const [m, y] = (date ?? '').split('-')
    return `${MONTH[parseInt(m)] ?? ''} - ${y ?? ''}`
}

const KpiChart = ({ kpi, labels, values, loading }) => {
    const unitSuffix = kpi.unit ? ` ${kpi.unit}` : ''
    // Valeurs arrondies à 2 décimales à l'affichage (les moyennes en jours ouvrés / taux ont des décimales).
    const fmt = (v) => `${formatMilliers(round(v, 2), true)}${unitSuffix}`
    // Delta signé vs mois précédent, arrondi à 2 décimales (évite aussi le bruit de soustraction de flottants).
    const fmtDelta = (v) => {
        const r = round(v, 2)
        return `${r > 0 ? '+' : ''}${formatMilliers(r, true)}${unitSuffix}`
    }

    // Label de colonne : valeur du mois + évolution vs mois précédent (2e ligne, plus discrète).
    const barLabel = (params) => {
        const cur = values[params.dataIndex]
        if (!Number.isFinite(cur)) {
            return ''
        }
        const prev = values[params.dataIndex - 1]
        if (!Number.isFinite(prev)) {
            return `{main|${fmt(cur)}}`
        }
        return `{main|${fmt(cur)}}\n{delta|${fmtDelta(cur - prev)}}`
    }

    const options = {
        // Titre porté par ECharts (et non par un header React) pour qu'il soit inclus dans l'image
        // lors d'un « enregistrer l'image » sur le canvas.
        title: { text: kpi.label, left: 'center', top: 8, textStyle: { fontSize: 14 } },
        grid: { top: 58, bottom: 40, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const header = params[0]?.axisValue ?? ''
                const value = params[0]?.value
                return `${header}<br/>${Number.isFinite(value) ? fmt(value) : '-'}`
            },
        },
        xAxis: {
            type: 'category',
            data: labels,
            axisLabel: { interval: 0, rotate: 40 },
        },
        yAxis: {
            type: 'value',
            max: ({ max }) => {
                const base = Number.isFinite(max) ? max : 0
                return base > 0 ? Math.ceil(base * 1.15) : undefined
            },
            axisLabel: { formatter: (v) => formatMilliers(v) },
        },
        series: [{
            name: kpi.label,
            type: 'bar',
            data: values,
            label: {
                show: true,
                position: 'top',
                formatter: barLabel,
                rich: {
                    main: { fontWeight: 'bold', fontSize: 12, lineHeight: 15 },
                    delta: { fontSize: 10, color: '#757575', lineHeight: 13 },
                },
            },
        }],
    }

    return (
        <Card sx={{ position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 1 }}>
                <Tooltip
                    title={<span style={{ whiteSpace: 'pre-line' }}>{kpi.description}</span>}
                    slotProps={{ popper: { sx: { zIndex: theme => `${theme.zIndex.modal + 1} !important` } } }}
                >
                    <InfoOutlinedIcon fontSize='small' sx={{ color: 'action.active', cursor: 'help' }} />
                </Tooltip>
            </Box>
            <ReactECharts
                option={options}
                notMerge={true}
                lazyUpdate={true}
                showLoading={loading}
                style={{ height: '300px' }}
            />
        </Card>
    )
}

KpiChart.propTypes = {
    kpi: PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        unit: PropTypes.string,
        description: PropTypes.string,
    }),
    labels: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.number),
    loading: PropTypes.bool,
}

const SupportEvolutionDialog = ({
    open,
    onClose,
    filter,
}) => {
    const [status, setStatus] = useState(STATUS.IDLE)
    const [series, setSeries] = useState([])
    // Projets clients (union MANTIS_NAME des instances produits) calculés une seule fois.
    // null = pas encore résolu ; [] = résolu mais aucun projet client trouvé.
    const [clientProjects, setClientProjects] = useState(null)

    const { data: projects = [] } = useFetch(ApplicationConf.support.projects())
    const { data: { instances = [] } = {} } = useFetch(ApplicationConf.dashboard())

    // Déduit « tous les projets clients » : pour chaque instance produit, on lit son paramètre
    // MANTIS_NAME (liste d'IDs) que l'on mappe vers les noms de projets Mantis.
    useEffect(() => {
        if (!projects.length || !instances.length) {
            return undefined
        }
        let cancelled = false
        const productInstances = instances.filter(i => CLIENT_PRODUCTS.includes(i.instanceType))
        Promise.all(productInstances.map(i =>
            aquaFetch(ApplicationConf.instance.single(i.instanceId))
                .then(inst => inst?.parameters ?? [])
                .catch(() => []),
        )).then(paramsArrays => {
            if (cancelled) {
                return
            }
            const projectIds = paramsArrays
                .flat()
                .filter(p => p.parameter === 'MANTIS_NAME')
                .flatMap(p => (p.value ?? '').split(',').map(v => Number(v)))
                .filter(id => Number.isFinite(id))
            const names = projects.filter(p => projectIds.includes(p.id)).map(p => p.name)
            setClientProjects(uniq(names))
        })
        return () => {
            cancelled = true
        }
    }, [projects, instances])

    // Un seul appel : la série mensuelle (6 indicateurs) est calculée côté backend. On attend d'avoir
    // résolu les projets clients (pour le délai moyen #5) avant de lancer la requête.
    useEffect(() => {
        if (!open || clientProjects === null) {
            return undefined
        }
        const controller = new AbortController()
        const { signal } = controller
        setStatus(STATUS.LOADING)
        aquaFetch(ApplicationConf.support.statisticsEvolution(), {
            method: 'POST',
            body: {
                startDate: filter.startDate,
                endDate: filter.endDate,
                projects: filter.projects,
                clientProjects,
            },
            signal,
        }).then(data => {
            if (!signal.aborted) {
                setSeries(Array.isArray(data) ? data : [])
                setStatus(STATUS.SUCCEEDED)
            }
        }).catch(() => {
            if (!signal.aborted) {
                setStatus(STATUS.REJECTED)
            }
        })
        return () => controller.abort()
    }, [open, clientProjects, filter])

    const loading = status === STATUS.LOADING || (open && clientProjects === null)
    const labels = series.map(s => monthLabel(s.date))

    return (
        <Dialog open={open} onClose={onClose} maxWidth='xl' fullWidth>
            <BasicDialogTitle onClose={onClose}>
                {'Évolution mensuelle du support'}
            </BasicDialogTitle>
            <DialogContent>
                {status === STATUS.REJECTED && series.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                        <Typography color='error'>{'Erreur lors du chargement de l\'évolution.'}</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2} alignItems='flex-start' sx={{ paddingTop: 1 }}>
                        {SUPPORT_KPIS.map(kpi => (
                            <Grid key={kpi.key} size={4}>
                                <KpiChart
                                    kpi={kpi}
                                    labels={labels}
                                    values={series.map(s => s[kpi.key])}
                                    loading={loading}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    )
}

SupportEvolutionDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    filter: PropTypes.shape({
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        projects: PropTypes.arrayOf(PropTypes.string),
        categories: PropTypes.arrayOf(PropTypes.string),
    }),
}

export default SupportEvolutionDialog
